// candidateRoleAndCompany returns the headline { role, company } for a candidate
// as a CONSISTENT PAIR — the role and company always describe the same job.
//
// Why a helper: the headline was previously assembled inline in several places,
// each combining the role from one field with the company from a *different*
// field. That produced "Frankenstein" headlines — e.g. a candidate whose
// operating role was "Konzern-Vorstand Brands and Retail" at Otto Group, but
// whose most-recent career row is an advisory seat at "World Retail Congress",
// would render "Konzern-Vorstand Brands and Retail at World Retail Congress":
// the title from one job glued to the company of another.
//
// Precedence (each tier is taken as a whole, never mixed across tiers):
//   1. primaryRole{Title,Company} — the LLM-derived "operative primary role",
//      a matched pair (the enrichment search-profile phase picks the real
//      operating role over board/advisory seats and pairs it with its employer).
//   2. The most-recent career entry (role + companyName from the same row).
//   3. The raw imported `title` (vCard TITLE / LinkedIn headline), no company.
//
// The returned `isFormer` flag lets callers mark a headline as a *past* role
// (render a localized "prev."/"ehem." prefix) instead of silently presenting an
// ended role as if it were current. See isFormerRole below for the rule.
export function candidateRoleAndCompany(c) {
  if (!c) return { role: "", company: "", companyId: null, isFormer: false };
  const isFormer = isFormerRole(c);
  if (c.primaryRoleTitle) {
    const company = c.primaryRoleCompany ?? "";
    return { role: c.primaryRoleTitle, company, companyId: companyIdForName(c, company), isFormer };
  }
  const latest = c.career?.[0];
  if (latest?.role)
    return {
      role: latest.role,
      company: latest.companyName ?? "",
      companyId: latest.companyId ?? null,
      isFormer,
    };
  return { role: c.title ?? "", company: "", companyId: null, isFormer };
}

// companyIdForName resolves the DISPLAYED company string back to a Company id, so the
// headline can link to /companies/{id}.
//
// Tier 2 needs no resolution — the name and the id come from the same career row, so they
// agree by construction. Tier 1 does: `primaryRoleCompany` is a free-text string the LLM
// paired with the role, carrying no id of its own. We therefore match it by NAME against
// the career rows and only accept a row that has an id.
//
// The name match is the safety rule, not a convenience. A link is only correct when it
// leads to the company whose name is on screen; an id borrowed from a different row would
// render "Company A" and navigate to Company B, which is worse than no link at all. So the
// comparison is deliberately narrow — casefolded and whitespace-collapsed, nothing more.
// No id means no link, and the name renders as plain text (the common case for
// import-only contacts, who have no career rows).
function companyIdForName(c, name) {
  const key = normalizeCompanyKey(name);
  if (!key) return null;
  const hit = (c.career ?? []).find(
    (e) => e?.companyId && normalizeCompanyKey(e.companyName) === key
  );
  return hit?.companyId ?? null;
}

function normalizeCompanyKey(v) {
  return (v ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

// candidateHeadline formats the { role, company, isFormer } pair into ONE
// localized display string — "prev. Role at Company" / "ehem. Rolle bei Firma" —
// for the compact one-line contexts (pipeline cards, the project drawer) where
// the profile's separate role/company spans aren't needed. It takes `t` as a
// parameter so this module keeps no i18n import; localization stays at the edge.
// Same precedence and same isFormer prefix as the profile header, so every
// surface that names a candidate's role agrees.
export function candidateHeadline(c, t) {
  const { role, company, isFormer } = candidateRoleAndCompany(c);
  if (!role) return "";
  const roleLabel = isFormer ? `${t("role_former_prefix")} ${role}` : role;
  return company ? `${roleLabel} ${t("at_company", { company })}` : roleLabel;
}

// A career period is "open" (the role is still held) when its end is blank or a
// present-literal. Empty is the canonical ongoing marker (the CV parser is told
// to leave `to` empty for current roles); "present"/"heute" are legacy literals
// still honoured in stored data and elsewhere in the UI.
const PRESENT_LITERALS = ["present", "heute"];
function isOpenEnd(to) {
  const t = (to ?? "").trim().toLowerCase();
  return t === "" || PRESENT_LITERALS.includes(t);
}

// careerDate renders one stored career date for display. LinkedIn-sourced entries
// are stored as sortable, language-free "2022-04"; CV-sourced ones keep whatever the
// document said ("04/2022"). Showing the first shape raw put two notations in the same
// list, so normalize the one known shape and leave everything else (bare years, free
// text) exactly as stored. Mirrors CareerDateFormatter.Display on the backend, which
// does the same for generated documents.
export function careerDate(value) {
  const v = (value ?? "").trim();
  const m = /^(\d{4})-(\d{1,2})$/.exec(v);
  return m ? `${m[2].padStart(2, "0")}/${m[1]}` : v;
}

// isFormerRole is true when the candidate has career history and NONE of it is a
// currently-held role — every entry has a closed end date. It is computed from
// the career data regardless of which headline tier was used, so it self-heals:
// add a current role and the flag clears, and a stale primaryRole left over from
// an earlier enrichment is corrected by the ground-truth career periods that
// documents render from too.
//
// Candidates with no career history (tier 3, raw imported title) can't be judged
// and are never marked former — we under-label rather than risk branding a
// sitting executive as past, since this label also flows to client-facing views.
export function isFormerRole(c) {
  const career = c?.career;
  if (!career?.length) return false;
  return !career.some((e) => isOpenEnd(e?.to));
}

// isCurrentAtCompany is true when the candidate still holds a role at the given
// company. Built on the same isOpenEnd rule as isFormerRole, so "current" means
// the same thing here as everywhere else. Checks every entry, not just the most
// recent one — an open board/advisory seat at the company counts even when the
// candidate's newest career row is elsewhere.
export function isCurrentAtCompany(c, companyId) {
  if (!companyId) return false;
  return (c?.career ?? []).some((e) => e?.companyId === companyId && isOpenEnd(e?.to));
}
